import jsPDF from "jspdf";
import { PrintDataEntitas } from "@/domain/entities/Booking";
import JsBarcode from "jsbarcode";
import uuid from "@/app/infrastructures/misc/common-library/Uuid";
import { vendorPickupDetail } from "@/app/ui/modules/receipt-v2/module";
import {
  MultilingualText,
  renderMultilingualText
} from "@/app/infrastructures/misc/js-pdf copy/render-multilingual-text";
import { priorityPrint, JIFEE_LOGO } from "../../constants/images";

const shiftX = (x: number) => x - 2;

const maskPhoneLast4 = (phone: any) => {
  const digits = String(phone || "").replace(/\D/g, "");
  const last4 = digits.slice(-4);
  return last4 ? `****${last4}` : "";
};

const resolveFontStyle = (fontName: string, style: string) => {
  if (fontName === "Roboto Mono") {
    if (style === "bold") return "bold";
    if (style === "semibold") return "semibold";
    return "normal";
  }

  if (style === "extrabold") return "extrabold";
  if (style === "bold") return "bold";
  if (style === "semibold") return "semibold";
  return "medium";
};

const createBadge = (doc: jsPDF | any, text: string, x: number, y: number) => {
  doc.setFontSize(9);
  doc.setDrawColor(0);
  doc.setFillColor(0, 0, 0);

  const w = doc.getTextDimensions(text).w + 2.3;
  const h = doc.getTextDimensions(text).h + 1.9;

  doc.roundedRect(x, y, w, h, 1, 1, "F");

  doc.setTextColor("#FFFFFF");
  doc.setFont("Inter", "bold");
  doc.text(text, x + 1, y + 3.6);
  return { w, h };
};

const buildDestinationLine = (data: PrintDataEntitas) => {
  const parts = [
    //data.sttDestinationSubDistrictName,
    data.sttDestinationDistrictName,
    //data.sttDestinationCityName,
    //data.sttDestinationProvinceName
  ]
    .map((v) => String(v || "").trim())
    .filter(Boolean);

  const dedup: string[] = [];
  parts.forEach((p) => {
    if (!dedup.includes(p)) dedup.push(p);
  });
  return dedup.join(", ").toUpperCase();
};

const buildRecipientCityPostal = (data: PrintDataEntitas) => {
  const city = String(data.sttDestinationCityName || "").trim();
  const postal = String(data.sttPostalCode || "").trim();
  if (!city && !postal) return "";
  return `${city}${postal ? ` ${postal}` : ""}`.trim().toUpperCase();
};

export const deliverySectionV3 = async (
  params: {
    doc: jsPDF | any;
    bookingType: string;
    accountType: string;
    data: PrintDataEntitas;
    pieceData: any;
    barcode: string;
    vendorRefCode: string;
    barcodeValue: string;
    vendorRefCodeValue: string;
    logo: string;
    shipmentId: string;
    startX: number;
    startY: number;
  },
  translate: Function,
  language = "id"
) => {
  const leftMargin = shiftX(params.startX + 5);
  const rightEndX = shiftX(params.startX + 77);
  const headerBottomY = params.startY + 48;
  const lineHeightSm = 2.8;

  params.doc.setTextColor("#333333");
  const product = String(params.data.sttProductType || "");

  const senderTopY = headerBottomY + 6;
  const senderName = String(params.data.sttSenderName || "").toUpperCase();
  const senderMaskedPhone = maskPhoneLast4(params.data.sttSenderPhone);
  const senderText =
    String(translate("receipt.shipper")).toUpperCase() +
    ": " +
    [senderName, senderMaskedPhone].filter(Boolean).join(" ");

  const senderBlock = await renderMultilingualText(
    new MultilingualText({
      doc: params.doc,
      text: senderText,
      defaultFont: "Inter",
      product,
      startX: leftMargin,
      startY: senderTopY,
      maxWidth: 70,
      fontStyle: "bold",
      fontSize: 6.2
    })
  );

  let cursorY = senderTopY + senderBlock.height + 2;
  const receiverTopY = cursorY;
  const recipientName = String(
    params.data.sttRecipientName || ""
  ).toUpperCase();
  const recipientMaskedPhone = maskPhoneLast4(params.data.sttRecipientPhone);
  const recipientAddress = String(params.data.sttRecipientAddress || "")
    .toUpperCase()
    .trim();
  const recipientCityPostal = buildRecipientCityPostal(params.data);

  const recipientTail = [recipientName, recipientMaskedPhone]
    .filter(Boolean)
    .join(" ");
  const recipientText =
    String(translate("receipt.receiver")).toUpperCase() +
    ": " +
    [recipientTail, recipientAddress, recipientCityPostal]
      .filter(Boolean)
      .join(", ");

  const recipientBlock = await renderMultilingualText(
    new MultilingualText({
      doc: params.doc,
      text: recipientText,
      defaultFont: "Inter",
      product,
      startX: leftMargin,
      startY: receiverTopY,
      maxWidth: 70,
      fontStyle: "bold",
      fontSize: 6.2
    })
  );

  cursorY = receiverTopY + recipientBlock.height;

  const districtPositionY = cursorY + 7;
  const destinationLine = buildDestinationLine(params.data);
  const destinationBlock = await renderMultilingualText(
    new MultilingualText({
      doc: params.doc,
      text: destinationLine,
      defaultFont: "Inter",
      product,
      startX: leftMargin,
      startY: districtPositionY,
      maxWidth: 70,
      fontStyle: "bold",
      fontSize: 8.2,
      maxLines: 2
    })
  );

  cursorY = districtPositionY + destinationBlock.height;

  const endContentY = params.startY + 105;
  const barcodeHeight = 7;
  const barcodeBottomPadding = 2;
  const barcodePositionY = endContentY - barcodeBottomPadding - barcodeHeight;

  const sttPositionYBase = cursorY + 1.2;
  const rawBadges = (params.data.sttFlag || [])
    .map((v: any) =>
      String(v || "")
        .trim()
        .toUpperCase()
    )
    // indikator woodpacking pindah ke badge PK (top/bottom section) — GENESIS-24600/GENESIS-24602
    .filter((v: string) => v !== "KAYU");
  if (params.data.sttIsRts) rawBadges.push("RTS");

  const uniqueBadges = Array.from(new Set(rawBadges)).filter(Boolean);
  let maxBadgeHeight = 0;

  const sttCommodityNameLanguage =
    language === "en"
      ? params.data.sttCommodityDescription
      : params.data.sttCommodityName;
  const sttCommodityNameString = String(sttCommodityNameLanguage || "");
  const sttCommodityUpper = sttCommodityNameString.toUpperCase();

  params.doc.setTextColor("#333333");
  params.doc.setFontSize(5.5);
  params.doc.setFont("Inter", "medium");
  let commodityLines = params.doc.splitTextToSize(sttCommodityUpper, 70);
  if (commodityLines.length > 2) {
    commodityLines = commodityLines.slice(0, 2);
    commodityLines[1] = `${String(commodityLines[1]).substring(0, 35)}...`;
  }

  params.doc.setFontSize(9);
  uniqueBadges.forEach((item: string) => {
    params.doc.setFont("Inter", "bold");
    const h = params.doc.getTextDimensions(item).h + 1.9;
    maxBadgeHeight = Math.max(maxBadgeHeight, h);
  });
  if (params.data.isPriorityTier) maxBadgeHeight = Math.max(maxBadgeHeight, 5);

  const commodityTopYBase = sttPositionYBase + maxBadgeHeight + 3.5;
  const commodityBottomYBase =
    commodityTopYBase + commodityLines.length * lineHeightSm;
  const reservedBottomY = barcodePositionY - 1.2;
  const shiftUp = Math.max(0, commodityBottomYBase - reservedBottomY);

  const sttPositionY = sttPositionYBase - shiftUp;
  const commodityTopY = commodityTopYBase - shiftUp;

  let sttPositionX = leftMargin;
  uniqueBadges.forEach((item: string) => {
    const rect = createBadge(params.doc, item, sttPositionX, sttPositionY);
    sttPositionX += rect.w + 2;
  });

  if (params.data.sttVendorCode === "JF") {
    const jifeeLogoProperties = params.doc.getImageProperties(JIFEE_LOGO);
    const dhlLogoAspectRatio =
      jifeeLogoProperties.height / jifeeLogoProperties.width;

    const jfX = rightEndX - 20 - 1;

    params.doc.addImage(
      JIFEE_LOGO,
      "PNG",
      jfX,
      barcodePositionY + 2,
      19,
      dhlLogoAspectRatio * 19,
      "jifee-logo",
      "FAST",
      0
    );
  }

  if (params.data.isPriorityTier) {
    params.doc.addImage(
      priorityPrint,
      "PNG",
      sttPositionX,
      sttPositionY,
      4.8,
      4.8,
      "logo-priority-print",
      "FAST"
    );
  }

  params.doc.setTextColor("#333333");
  params.doc.setFontSize(5.5);
  params.doc.setFont("Inter", "bold");
  params.doc.text(commodityLines, leftMargin, commodityTopY);
  cursorY = commodityTopY + commodityLines.length * lineHeightSm;
  const awbPositionY = barcodePositionY - 0.8;
  const sttVendorRefferenceNo = String(params.data.sttVendorRefferenceNo || "");
  const awbText =
    sttVendorRefferenceNo || String(params.vendorRefCodeValue || "");

  if (awbText) {
    params.doc.setFont("Inter", "bold");
    params.doc.setFontSize(9.5);
    params.doc.text(awbText, leftMargin, awbPositionY);

    const canvas = document.createElement("canvas");
    JsBarcode(canvas, awbText, {
      displayValue: false,
      margin: 0
    });
    const jpegUrl = canvas.toDataURL("image/jpeg");
    params.doc.addImage(
      jpegUrl,
      "PNG",
      leftMargin,
      barcodePositionY,
      params.doc.getTextDimensions(awbText).w + 13,
      7,
      uuid(),
      "FAST",
      0
    );
  }

  const vendorName = vendorPickupDetail(
    params.data.sttVendorName,
    params.data.sttDistrictType
  );
  if (vendorName) {
    const badgeText = String(vendorName || "").toUpperCase();
    const dims = params.doc.getTextDimensions(badgeText);
    const badgeW = dims.w + 2.3;
    const badgeX = rightEndX - badgeW - 2;
    createBadge(params.doc, badgeText, badgeX, barcodePositionY + 2);
  }

  // line before cut the footer
  params.doc.setLineDash([], 0);
  params.doc.setLineWidth(0.4);
  params.doc.setDrawColor("#000000");
  params.doc.line(rightEndX, headerBottomY, rightEndX, endContentY);
  params.doc.line(
    params.startX + 0,
    endContentY,
    params.startX + 106,
    endContentY
  );

  return {
    ...params,
    startX: rightEndX,
    startY: endContentY
  };
};
